import { apiPrefix, appName, baseURL, scope } from '@/config/base';
import { useLocales } from '@/Locales';
import {
  httpImageProcessOcrDetail,
  httpReformatGroupExtract,
  httpReformatMarkdownParse,
} from '@/services/imageProcess';
import { getToken } from '@/utils/cookies';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { message } from '@sinohealth/butterfly-ui-components';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { OcrContext } from '../detail/components/PatientTabs/components/TabItem';

export const useSseSocket = () => {
  const [sseParams, setSseParams] = useState<any>();
  const token = getToken();
  const [abortController, setAbortController] = useState(new AbortController());
  const { setOcrDetail, reportText, setFinished, setReportText } = useContext(OcrContext);
  const { t } = useLocales();
  const [connectLoading, setConnectLoading] = useState(false); // 是否正在连接
  const [textLoading, setTextLoading] = useState(false);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [error, setError] = useState(false);
  const retryCountRef = useRef(0); // 重试次数计数器

  // 通信事件
  const contact = async () => {
    // messageData = { ...messageData, do_stream: modelArg.do_stream }; // 请求参数
    // receivedDataRef.current = '';

    setTextLoading(true);
    setConnectLoading(true);
    setReportText('');
    setFinished(false);
    setError(false);
    setOcrDetail([]);
    fetchEventSource(`${baseURL}${apiPrefix}/reformat/report/stream`, {
      method: 'POST',
      // 添加请求头
      headers: {
        'X-Token': token || '',
        biz: scope,
        scope,
        app: appName,
        'Content-Type': 'application/json',
      },
      // 传参必须保证是json
      body: JSON.stringify(sseParams),
      // abortController.signal 提供了一个信号对象给 fetchEventSource 函数。
      // 如果在任何时候你想取消正在进行的 fetch 操作，你可以调用
      // abortController.abort()。这会发出关联任务的信号，你可以使用
      // AbortController 的信号来检查异步操作是否已被取消。
      signal: abortController.signal,
      openWhenHidden: true, // 切换标签页时连接不关闭
      async onopen(response) {
        console.log('response', response);
        if (response.ok) {
          // 连接成功
          setConnectLoading(false);
          console.log('连接成功');
        } else {
          console.log('连接失败');
          setTextLoading(false);
          setConnectLoading(false);
          setJsonLoading(false);
          setError(true);
        }
      },
      onmessage(msg: { data: any; event: string }) {
        // const eventType = msg.event; // 监听event的具名事件
        // console.log({ msg });
        const data = JSON.parse(msg.data || '{}');
        const { content, finished } = data;
        !finished && setReportText((prev: any) => `${prev}${content || ''}`);
        setFinished(finished);
        // console.log({ content, finished });
        if (finished) {
          setTextLoading(false);
          setJsonLoading(true);
          const jsonContent = JSON.parse(content);
          console.log({ jsonContent });
          httpReformatMarkdownParse({
            reportType: sseParams?.reportType,
            markdowns: [jsonContent.markdown],
          })
            .then((res: any) => {
              console.log('合并后的数据', res);
              if (sseParams?.reportType === 'inspectionReport') {
                const list = res?.structuredResults?.map((el: any) => {
                  return {
                    ...el,
                    zhInspectionItems: el?.zhInspectionItems || [],
                  };
                });
                // console.log({ list });
                setOcrDetail({
                  ...res,
                  structuredResults: list,
                });
              } else {
                setOcrDetail(res);
              }
              message.success(t('智能识别完成'));
            })
            .catch((err: any) => {
              console.log(err);
            })
            .finally(() => {});
        }
      },
      onclose() {
        // 正常结束的回调
        console.log('closed');
        setJsonLoading(false);
        abortController?.abort();
      },
      onerror(err) {
        closeSSE();
        setConnectLoading(false);
        console.log('Error occurred:', err);
        setError(true);
        // return 1; // 返回 true 表示重试
        retryCountRef.current += 1; // 增加重试次数
        if (retryCountRef.current < 5) {
          console.log(`重试次数: ${retryCountRef.current}`);
          // contact(); // 重试
          return 1; // 返回 true 表示重试
        }
        console.log('达到最大重试次数，停止重试');
        retryCountRef.current = 0; // 重置重试次数
        throw new Error('closed');
      },
    });
  };

  // 终止连接方法，比如在切换模型时，你可能有必要终止上一次连接来避免问答串联
  const closeSSE = () => {
    abortController?.abort();
    setJsonLoading(false);
    setTextLoading(false);
    setAbortController(new AbortController());
  };

  useEffect(() => {
    sseParams && contact();

    return () => {
      closeSSE();
    };
  }, [sseParams]);

  return {
    setSseParams,
    closeSSE,
    reportText,
    setReportText,
    textLoading,
    jsonLoading,
    setJsonLoading,
    error,
    connectLoading,
  };
};

/**
 * @description: 使用视觉大模型对同类型报告进行分组并且对每份报告的章
 * @author: KaifengLi
 * @version: v2.32.0
 * @Date: 2025-04-21 15:15:43
 */
export const useFieldStream = (callback?: (g: any[]) => void) => {
  const token = getToken();
  const { t } = useLocales();

  const retryCountRef = useRef(0); // 重试次数计数器

  // 终止连接方法，比如在切换模型时，你可能有必要终止上一次连接来避免问答串联
  const [abortController, setAbortController] = useState<any>({});
  const [fieldStreamParams, setFieldStreamParams] = useState<any>(); // 参数
  const [fieldStreamLoading, setFieldStreamLoading] = useState(false); // 加载中
  const [fieldStream, setFieldStream] = useState<any[]>([]); // 识别的文本
  const [mergeFields, setMergeFields] = useState<any[]>([]); // 合并字段
  const [allReqLoading, setAllReqLoading] = useState<any[]>([]); // 所有请求loading
  const [reformatGroup, setReformatGroup] = useState<any[]>([]); // 章节分组
  const [chapterExtractFields, setChapterExtractFields] = useState<any>([]); // 章节抽取字段
  const [canExtractFields, setCanExtractFields] = useState<boolean>(false); // 可以抽取的字段
  const [fieldsToLabel, setFieldsToLabel] = useState<any>({}); // 可以抽取的字段

  // 分组抽取字段
  const httpReformatGroupExtractReq = async () => {
    try {
      setFieldStreamLoading(true);
      handleReset();
      const res: any = await httpReformatGroupExtract(fieldStreamParams);
      console.log(res);
      const { groups, fields } = res || {};
      setReformatGroup(groups || []);
      setChapterExtractFields(fields || []);
      if (callback) {
        callback(groups);
      }
      // 将 fields 数组转换为 code: name 的对象
      const fieldsLabelMap = (fields || []).reduce(
        (acc: any, field: { code: string; name: string }) => {
          acc[field.code] = field.name;
          return acc;
        },
        {},
      );
      console.log({ fieldsLabelMap });
      setFieldsToLabel(fieldsLabelMap); // 更新状态

      // 初始化fieldStream
      const fieldArray = Array.from({ length: groups?.length || 0 }, () => {
        return fields?.reduce(
          (acc: any, el: any) => {
            acc[el.code] = '';
            return acc;
          },
          { mergeFields: '' },
        );
      });
      setFieldStream([...fieldArray]); // 使用新的数组
      const loadingArr = Array.from({ length: groups?.length || 0 }, () => {
        return fields?.reduce(
          (acc: any, el: any) => {
            acc[el.code] = true;
            return acc;
          },
          { mergeFields: true },
        );
      });
      setAllReqLoading(loadingArr);
      console.log({ fieldArray, loadingArr }); // todo

      if (groups?.length && fields?.length) {
        groups.forEach((el: any, index: number) => {
          fields.forEach((field: any) => {
            setAbortController({
              ...abortController,
              [field.name]: new AbortController(),
            });

            contact(
              {
                recognitionTaskId: fieldStreamParams?.recognitionTaskId, // 识别任务编号
                patientId: fieldStreamParams?.patientId, // 患者编号
                reportType: fieldStreamParams?.reportType, // 报告类型
                fieldExtractTaskId: el?.fieldExtractTaskId, // 字段抽取任务
                fieldCodes: [field?.code], // 字段名称
                imagePaths: el?.imagePaths, // 图片路径信息
                code: field?.code, // 字段编码
              },
              index,
              fieldArray,
              loadingArr,
            );
          });
        });
      } else {
        setFieldStreamLoading(false);
      }
    } catch (error) {
      console.log(error);
      setFieldStreamLoading(false);
    }
  };

  const handleReset = () => {
    setFieldStream([]);
    setMergeFields([]);
    setAllReqLoading([]);
    setReformatGroup([]);
    setChapterExtractFields([]);
  };

  // 通信事件
  const contact = async (params: any, index: number, arr: any, load: any) => {
    fetchEventSource(`${baseURL}${apiPrefix}/reformat/field/stream`, {
      method: 'POST',
      // 添加请求头
      headers: {
        'X-Token': token || '',
        biz: scope,
        scope,
        app: appName,
        'Content-Type': 'application/json',
      },
      // 传参必须保证是json
      body: JSON.stringify(params),
      // abortController.signal 提供了一个信号对象给 fetchEventSource 函数。
      // 如果在任何时候你想取消正在进行的 fetch 操作，你可以调用
      // abortController.abort()。这会发出关联任务的信号，你可以使用
      // AbortController 的信号来检查异步操作是否已被取消。
      signal: abortController?.[params.code]?.signal,
      openWhenHidden: true, // 切换标签页时连接不关闭
      async onopen(response) {
        // console.log('response', response);
        if (response.ok) {
          // 连接成功
          console.log('连接成功');
          retryCountRef.current = 0; // Reset retry count on successful connection
        } else {
          console.log('连接失败');
          retryCountRef.current += 1;
          if (retryCountRef.current < 3) {
            console.log(`连接失败，将在1秒后重试第 ${retryCountRef.current} 次`);
            contact(params, index, arr, load);
          } else {
            console.log('达到最大重试次数，停止重试');
            if (params?.code !== 'mergeFields') {
              setMergeFields((prev: any[]) => {
                const updatedMergeFields = [...prev];
                if (!updatedMergeFields[index]) {
                  updatedMergeFields[index] = [];
                }
                updatedMergeFields[index].push(params?.code);
                console.log('停止重试', { updatedMergeFields });
                return updatedMergeFields;
              });
              const array = [...load]; // 创建新的数组
              array[index][params?.code] = false;
              // console.log({ array });
              setAllReqLoading(array); // 更新状态
            }
            retryCountRef.current = 0; // Reset after max retries
            // Optionally handle max retries error state here
          }
        }
      },
      onmessage(msg: { data: any; event: string }) {
        // console.log({ msg });
        const data = JSON.parse(msg.data || '{}');
        const { content, finished } = data;
        // console.log({ content, finished });
        if (finished) {
          // 更新allReqLoading中对应字段的状态
          if (params?.code !== 'mergeFields') {
            const array = [...load]; // 创建新的数组
            array[index][params?.code] = false;
            // console.log({ array });
            setAllReqLoading(array); // 更新状态
          } else {
            // 创建新的数组以避免直接修改状态
            const updatedReqLoading = allReqLoading.map((item) => ({
              ...item,
              mergeFields: false,
            }));
            setAllReqLoading(updatedReqLoading);
          }

          if (!content) {
            // 如果content为空,将code添加到mergeFields中
            if (params?.code !== 'mergeFields') {
              setMergeFields((prev: any[]) => {
                const updatedMergeFields = [...prev];
                if (!updatedMergeFields[index]) {
                  updatedMergeFields[index] = [];
                }
                updatedMergeFields[index].push(params?.code);
                return updatedMergeFields;
              });
            }
            console.log({ mergeFields, allReqLoading });
            return;
          }
          const jsonContent = JSON.parse(content);

          if (params?.code === 'mergeFields') {
            // console.log({ jsonContent });
            const updatedArray = [...fieldStream]; // 创建新的数组
            updatedArray[index][params?.code] = jsonContent?.markdown;
            // console.log({ arr, updatedArray });
            setFieldStream(updatedArray); // 更新状态
            return;
          }
          const updatedArray = [...arr]; // 创建新的数组
          updatedArray[index][params?.code] = jsonContent?.markdown;
          // console.log({ arr, updatedArray });
          setFieldStream(updatedArray); // 更新状态
        } else {
          if (!content) {
            // 如果content为空,将code添加到mergeFields中
            return;
          }
          // const jsonContent = JSON.parse(content);
          // console.log({ content });
          const updatedArray = [...arr]; // 创建新的数组
          updatedArray[index][params?.code] = `${updatedArray[index][params?.code]}${content}`;
          // console.log({ arr, updatedArray });
          setFieldStream(updatedArray); // 更新状态
        }
      },
      onclose() {
        // 正常结束的回调
        console.log('closed');
        abortController?.[params.code]?.abort();
      },
      onerror(err) {
        closeSSE();
        console.log('Error occurred:', err);

        // return 1; // 返回 true 表示重试
        retryCountRef.current += 1; // 增加重试次数
        if (retryCountRef.current < 5) {
          console.log(`重试次数: ${retryCountRef.current}`);
          return 1; // 返回 true 表示重试
        }
        console.log('达到最大重试次数，停止重试');
        retryCountRef.current = 0; // 重置重试次数
        throw new Error('closed');
      },
    });
  };

  // 终止连接方法，比如在切换模型时，你可能有必要终止上一次连接来避免问答串联
  const closeSSE = () => {
    // abortController?.abort();
    setAbortController(new AbortController());
  };

  useMemo(() => {
    // console.log({ allReqLoading, mergeFields });
    const allReq = [...allReqLoading];
    if (!allReq.length) return;
    // 检查是否有正在进行中的mergeFields请求
    // 检查每个对象中除了mergeFields外的所有字段是否都为false
    const trueList = allReq.map((obj: any) => {
      const otherFields = Object.entries(obj).filter(([key]) => key !== 'mergeFields');
      return otherFields.every(([_, value]) => value === false);
    });
    // 检查是否只有mergeFields为true
    const hasMergeFieldsLoading = allReq.some((obj: any) => {
      const otherFields = Object.entries(obj).filter(([key]) => key !== 'mergeFields');
      return obj.mergeFields && otherFields.every(([_, value]) => value === false);
    });

    // console.log({ trueList, hasMergeFieldsLoading });

    // 如果有mergeFields正在加载,直接返回
    if (hasMergeFieldsLoading && trueList.filter((el: any) => el).length === reformatGroup.length) {
      // 使用Set去重，只对有需要合并字段的组发起一次请求
      if (mergeFields.length) {
        const currReq = [...allReq];
        reformatGroup.forEach((fields: any, index: number) => {
          // console.log({ fields, index });
          if (mergeFields[index].length) {
            contact(
              {
                recognitionTaskId: fieldStreamParams?.recognitionTaskId,
                patientId: fieldStreamParams?.patientId,
                reportType: fieldStreamParams?.reportType,
                fieldExtractTaskId: reformatGroup[index]?.fieldExtractTaskId,
                fieldCodes: Array.from(new Set(mergeFields[index])),
                imagePaths: reformatGroup[index].imagePaths,
                code: 'mergeFields',
              },
              index,
              fieldStream,
              currReq,
            );
          }
        });
      } else {
        console.log('没有空字段，所有请求都已完成');
        setAllReqLoading([]);
        setMergeFields([]);
        setCanExtractFields(true);
        const timer = setTimeout(() => {
          setFieldStreamLoading(false);
          clearTimeout(timer);
        }, 500);
      }
      return;
    }

    // 检查是否所有请求都完成
    const allRequestsCompleted = allReq.every((obj: any) =>
      Object.values(obj).every((value) => value === false),
    );

    if (allRequestsCompleted) {
      console.log('所有请求都已完成');
      setAllReqLoading([]);
      setMergeFields([]);
      setCanExtractFields(true);
    }
  }, [allReqLoading]);

  useEffect(() => {
    if (fieldStreamParams) {
      httpReformatGroupExtractReq();
    }
  }, [fieldStreamParams]);

  return {
    fieldStreamParams,
    setFieldStreamParams,
    fieldStreamLoading,
    setFieldStreamLoading,
    fieldStream,
    setFieldStream,
    reformatGroup,
    chapterExtractFields,
    canExtractFields,
    fieldsToLabel,
    setCanExtractFields,
  };
};

export const useSSE = (url: string, params?: any) => {
  const [data, setData] = useState('');
  const [error, setError] = useState<any>();
  const [loading, setLoading] = useState(false);
  const controller = useRef(new AbortController());

  const token = getToken();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetchEventSource(url, {
          method: 'POST',
          headers: {
            'X-Token': token || '',
            biz: scope,
            scope,
            app: appName,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ query: '你的查询' }),
          ...params,
          signal: controller.current.signal,
          onmessage(ev) {
            setData(ev.data);
          },
          onclose() {
            console.log('Connection closed by server');
            setLoading(false);
          },
          onerror(err) {
            setError(err);
            console.error('Error received:', err);
            setLoading(false);
          },
        });
        setLoading(false);
      } catch (err) {
        setError(err);
        console.error('Fetch error:', err);
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      controller.current.abort();
    };
  }, [url, params]);

  return { data, error, loading };
};

/** 获取图片识别详情 */
export const useOcrDetail = () => {
  const [ocrDetailParams, setOcrDetailParams] = useState<any>();
  const [ocrRecogniText, setOcrRecogniText] = useState<any[]>([]); // 识别的文本
  const [ocrLoading, setOcrLoading] = useState(false); // 加载中

  const httpImageProcessOcrDetailReq = async () => {
    try {
      const res: any = await httpImageProcessOcrDetail(ocrDetailParams);
      // console.log(res);
      setOcrRecogniText(res || []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (ocrDetailParams) {
      if (ocrDetailParams.imagePaths.length) {
        httpImageProcessOcrDetailReq();
      } else {
        setOcrRecogniText([]);
      }
    }
    return () => {
      setOcrDetailParams(undefined);
    };
  }, [ocrDetailParams]);

  return { ocrDetailParams, setOcrDetailParams, ocrRecogniText, setOcrRecogniText };
};

export const markdownToJson = (markdown: string): Record<string, string> => {
  const lines: string[] = markdown.split('\n');
  const result: Record<string, string> = {};

  let currentTitle: string = '';
  let currentContent: string[] = [];

  lines.forEach((line, index) => {
    if (line.startsWith('### ')) {
      // 如果已有标题内容，先保存之前的内容
      if (currentTitle) {
        result[currentTitle] = currentContent.join('\n').trim();
        currentContent = [];
      }
      currentTitle = line.substring(4).trim();
    } else if (currentTitle && line.trim()) {
      currentContent.push(line.trim());
    }
  });

  // 保存最后一个标题的内容
  if (currentTitle) {
    result[currentTitle] = currentContent.join('\n').trim();
  }

  return result;
};
